import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { LoadingScreen } from '../components/feedback/LoadingScreen';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding, isLoading, error, projectName } = useBranding();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error Loading Platform</h1>
          <p className="text-gray-400">{error || 'Failed to load branding'}</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: branding.backgroundColor }}
    >
      {/* Header */}
      <header className="py-6 2xl:py-10 px-12 2xl:px-20 flex justify-between items-center">
        {branding.logoUrl && (
          <img 
            src={branding.logoUrl} 
            alt={`${projectName} logo`}
            className="2xl:h-32"
            style={{ height: 80, width: 'auto', objectFit: 'contain', display: 'block' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <button
          onClick={handleLogin}
          className="px-6 py-2 2xl:px-10 2xl:py-4 text-base 2xl:text-xl rounded-lg font-semibold transition-colors"
          style={{
            backgroundColor: branding.primaryColor,
            color: '#ffffff'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = branding.secondaryColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = branding.primaryColor;
          }}
        >
          Login
        </button>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex items-center justify-center px-12 2xl:px-20 py-16 2xl:py-24">
        <div className="max-w-4xl 2xl:max-w-6xl text-center">
          {branding.logoUrl && (
            <img 
              src={branding.logoUrl} 
              alt={`${projectName} logo`}
              className="h-24 2xl:h-40 mx-auto mb-8 2xl:mb-16 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          
          <h1 
            className="text-5xl lg:text-6xl 2xl:text-8xl font-bold mb-6 2xl:mb-10"
            style={{ color: branding.primaryColor }}
          >
            {branding.headingText}
          </h1>
          
          <h2 
            className="text-2xl lg:text-3xl 2xl:text-5xl mb-6 2xl:mb-10"
            style={{ color: branding.secondaryColor }}
          >
            {branding.subheadingText}
          </h2>
          
          <p 
            className="text-lg lg:text-xl 2xl:text-3xl mb-12 2xl:mb-20 max-w-3xl 2xl:max-w-5xl mx-auto leading-relaxed"
            style={{ color: '#666' }}
          >
            {branding.landingDescription}
          </p>

          <button
            onClick={handleLogin}
            className="px-12 py-4 2xl:px-20 2xl:py-6 rounded-lg text-lg 2xl:text-2xl font-semibold transition-all transform hover:scale-105"
            style={{
              backgroundColor: branding.primaryColor,
              color: '#ffffff'
            }}
          >
            Get Started
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 2xl:py-10 text-center text-sm 2xl:text-lg" style={{ color: '#999' }}>
        <p>&copy; {new Date().getFullYear()} {branding.headingText}. All rights reserved.</p>
      </footer>
    </div>
  );
};